import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const { user } = useAuth();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const nav = useNavigate();

  const routeFor = (n) => {
    switch (n.type) {
      case "interest":
      case "interest_update":
        return "/responses";
      case "message":
        return n.ref_id ? `/messages/${n.ref_id}` : "/messages";
      case "order":
      case "order_update":
      case "payment":
        return "/orders";
      case "review":
      case "boost":
        return `/marketplace/${n.ref_id}`;
      case "payout":
      case "wallet":
        return "/profile";
      default:
        return "/notifications";
    }
  };

  const onClickNotif = async (n) => {
    if (!n.read) {
      try { await api.post("/notifications/read-all"); }
      catch (e) { console.warn("[GTF] Failed to mark notifications as read:", e); }
    }
    nav(routeFor(n));
  };

  const load = () => api.get("/notifications").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-3xl">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-gray-500">{t("notif.activity")}</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{t("notif.title")}</h1>
          </div>
          <button data-testid="notif-read-all" onClick={readAll} className={`text-sm inline-flex items-center gap-2 ${theme.text} font-semibold`}><CheckCheck className="h-4 w-4" />{t("notif.mark_read")}</button>
        </div>

        <div className="mt-8 space-y-3">
          {items.length === 0 && (
            <div className="text-center py-16 rounded-3xl bg-white border border-gray-100">
              <Bell className="h-8 w-8 text-gray-300 mx-auto" />
              <div className="mt-3 text-gray-500">{t("notif.empty")}</div>
            </div>
          )}
          {items.map((n) => (
            <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => onClickNotif(n)}
              className={`w-full text-left flex items-start gap-4 rounded-2xl p-5 bg-white border transition-all hover:-translate-y-0.5 hover:shadow-lg ${n.read ? "border-gray-100" : theme.border}`}>
              <div className={`h-10 w-10 rounded-xl ${theme.bgSoft} ${theme.text} flex items-center justify-center flex-shrink-0`}>
                <Bell className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{n.title}</div>
                <div className="text-sm text-gray-600">{n.body}</div>
                <div className="text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read && <span className={`h-2 w-2 rounded-full ${theme.bg}`} />}
            </button>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
